export const STUDY_TYPE_OPTIONS = [
  { value: "Phase I", label: "Phase I" },
  { value: "Phase II", label: "Phase II" },
  { value: "Phase III", label: "Phase III" },
  { value: "Phase IV", label: "Phase IV" },
  { value: "Phase Observational", label: "Phase Observational" },
];

export const EMPTY_STUDY_METADATA = {
  startDate: "",
  endDate: "",
  studyType: null,
  protocolNumber: "",
  protocolVersion: "",
  studyDescription: "",
};

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getTomorrowIsoDate() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseIsoDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parts = raw.split("-").map((p) => Number(p));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [year, month, day] = parts;
  const d = new Date(year, month - 1, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

export function isValidProtocolVersion(value) {
  const raw = String(value || "").trim();
  if (!raw) return true;
  return /^\d+(\.\d+)?$/.test(raw);
}

export function formatStudyDuration(startDate, endDate) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  if (!start || !end || end <= start) return "";
  const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "";

  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const remDays = days - years * 365 - months * 30;
  const parts = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? "" : "s"}`);
  if (months > 0) parts.push(`${months} month${months === 1 ? "" : "s"}`);
  if (remDays > 0 || parts.length === 0) {
    parts.push(`${remDays} day${remDays === 1 ? "" : "s"}`);
  }
  return `${parts.join(", ")} (${days} days)`;
}

export function getStudyMetadataValidationErrors(
  fields,
  { isCreating = false } = {},
) {
  const errs = [];
  const startDate = String(fields?.startDate || "").trim();
  const endDate = String(fields?.endDate || "").trim();
  const protocolVersion = String(fields?.protocolVersion || "").trim();
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  const today = startOfToday();

  if (isCreating && startDate && start && start <= today) {
    errs.push("Start date must be a future date when creating a study.");
  }
  if (startDate && endDate && start && end && end <= start) {
    errs.push("End date must be after the start date.");
  }
  if (protocolVersion && !isValidProtocolVersion(protocolVersion)) {
    errs.push("Protocol version must use numbers and a dot only (e.g. 1.0).");
  }
  return errs;
}

export function mapStudyMetadataFromInfo(info = {}) {
  const pick = (...keys) => {
    for (const key of keys) {
      const val = info?.[key];
      if (val !== undefined && val !== null && String(val).trim() !== "") {
        return String(val).trim();
      }
    }
    return "";
  };

  const studyTypeRaw = pick("StudyType", "studyType");
  const studyType =
    STUDY_TYPE_OPTIONS.find(
      (opt) => opt.value.toLowerCase() === studyTypeRaw.toLowerCase(),
    ) || null;

  return {
    startDate: pick("StartDate", "startDate"),
    endDate: pick("EndDate", "endDate"),
    studyType,
    protocolNumber: pick("ProtocolNumber", "protocolNumber"),
    protocolVersion: pick("ProtocolVersion", "protocolVersion"),
    studyDescription: pick("StudyDescription", "studyDescription"),
  };
}

export function buildStudyMetadataPayload(fields = {}) {
  const studyType =
    fields.studyType &&
    (typeof fields.studyType === "object"
      ? fields.studyType.value
      : fields.studyType);

  return {
    StartDate: String(fields.startDate || "").trim(),
    EndDate: String(fields.endDate || "").trim(),
    StudyType: String(studyType || "").trim(),
    ProtocolNumber: String(fields.protocolNumber || "").trim(),
    ProtocolVersion: String(fields.protocolVersion || "").trim(),
    StudyDescription: String(fields.studyDescription || "").trim(),
  };
}
